import { Component, ElementRef, OnInit, ViewChild, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { mainConfig } from '@config/main.config';
import { AuthService } from '@services/auth.service';
import { UsersService } from '@services/users.service';
import { UtilsService } from '@services/utils.service';
import { iniziali } from '@services/iniziali.util';
import { PageComponent } from '../../../layout/page/page.component';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [PageComponent, mainConfig.material, mainConfig.formModules],
  templateUrl: './profile.component.html',
})
export class ProfileComponent implements OnInit {
  profileForm: FormGroup;

  readonly caricamento = signal(true);
  readonly salvataggioInCorso = signal(false);

  // Avatar
  imageName = signal('');
  imagePreview = signal('');
  @ViewChild('fileInput') fileInput: ElementRef | undefined;
  uploadSuccess = false;
  uploadError = false;

  constructor(
    public utilsService: UtilsService,
    private authService: AuthService,
    private usersService: UsersService,
    private snackBar: MatSnackBar
  ) {
    this.profileForm = new FormGroup({
      id: new FormControl<number | undefined>(undefined),
      blogger: new FormControl<string | undefined>(undefined),
      email: new FormControl<string | undefined>(undefined),
      url_google: new FormControl<string | undefined>(undefined),
      url_facebook: new FormControl<string | undefined>(undefined),
      url_twitter: new FormControl<string | undefined>(undefined),
      url_linkedin: new FormControl<string | undefined>(undefined),
      avatar: new FormControl<string | undefined>(undefined),
    });
  }

  /**
   * Prima i dati si riempivano dentro un `setTimeout` da un secondo, nella
   * speranza che `me` fosse tornato: su una connessione lenta il form restava
   * vuoto, su una veloce si aspettava per niente.
   */
  async ngOnInit(): Promise<void> {
    const utente = await this.authService.attendiUtente();
    if (!utente) {
      this.caricamento.set(false);
      return;
    }

    this.profileForm.patchValue({
      id: utente.id,
      blogger: utente.blogger,
      email: utente.email,
      url_google: utente.url_google,
      url_facebook: utente.url_facebook,
      url_twitter: utente.url_twitter,
      url_linkedin: utente.url_linkedin,
      avatar: utente.avatar,
    });
    // L'email non si cambia da qui: e' l'identita' con cui si entra.
    this.profileForm.controls['email'].disable();

    this.mostraAvatar(utente.avatar, utente.blogger);
    this.caricamento.set(false);
  }

  get iniziali(): string {
    return iniziali(this.profileForm.controls['blogger'].value);
  }

  save(): void {
    const raw = this.profileForm.getRawValue();
    delete raw.email;

    this.salvataggioInCorso.set(true);
    this.usersService.save(raw).subscribe({
      next: (res) => {
        this.salvataggioInCorso.set(false);
        this.authService._user = res;
        this.mostraAvatar(res.avatar, res.blogger);
        this.utilsService.notificationsService.showNotification('', 'Profilo salvato', 'success');
      },
      error: (err) => {
        this.salvataggioInCorso.set(false);
        this.utilsService.defaultHttpErrorHandler(err);
      },
    });
  }

  /**
   * Anteprima dell'avatar.
   *
   * Prima si costruiva un oggetto `File` finto a partire dall'indirizzo
   * dell'immagine, solo per avere qualcosa da mostrare nel template. Basta
   * l'indirizzo.
   */
  private mostraAvatar(indirizzo: string | undefined, nome: string | undefined): void {
    this.imagePreview.set(indirizzo ?? '');
    this.imageName.set(indirizzo ? (nome ?? 'avatar') : '');
  }

  onFileChange(event: any): void {
    this.uploadFile(event.target.files[0] as File | null);
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.uploadFile(event.dataTransfer?.files[0] as File | null);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  uploadFile(file: File | null): void {
    if (!file || !file.type.startsWith('image/')) {
      this.uploadSuccess = false;
      this.uploadError = true;
      this.snackBar.open('Puoi inserire solo immagini', 'Chiudi', { duration: 3000, panelClass: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Img = e.target?.result as string;
      this.imagePreview.set(base64Img);

      this.usersService.uploadAvatar(base64Img, file.name).subscribe({
        next: (response) => {
          this.profileForm.controls['avatar'].setValue(response.url);
          this.uploadSuccess = true;
          this.uploadError = false;
          // L'avatar si salva subito: e' un'azione conclusa in se'.
          this.save();
        },
        error: () => {
          this.snackBar.open("Errore durante il caricamento dell'avatar", 'Chiudi', {
            duration: 3000,
            panelClass: 'error',
          });
          this.uploadSuccess = false;
          this.uploadError = true;
        },
      });
    };
    reader.readAsDataURL(file);
    this.imageName.set(file.name);
  }

  removeImage(): void {
    this.imagePreview.set('');
    this.imageName.set('');
    this.uploadSuccess = false;
    this.uploadError = false;
    this.profileForm.controls['avatar'].setValue(null);
  }
}
